import { ReactNode, useCallback, useEffect } from 'react';
import { batch } from 'react-redux';

import { useDrawingShapeContext } from '../drawingShapeContext';
import { useUndoStack } from './hooks';
import { UndoStackContext } from './undoStack.context';
import { DoOperation, UndoStackContextType } from './undoStack.types';
import { addToStack, moveIndex } from './undoStack.utils';

import { useSelectedEdge } from '~/api/easy-estimator-api/edges-api/hooks/useSelectedEdge';
import { drawingTool } from '~/core/engine/DrawingTool';
import useAppliance from '~/core/hooks/useAppliance';
import { validateApplianceConsistency } from '~/core/utils/drawingTool/drawingTool.utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectSelectedColorId,
  updateSelectedColorId,
} from '~/redux/slices/color';
import {
  selectSelectedAppliance,
  setCookingAppliance,
} from '~/redux/slices/cookingAppliance';
import {
  selectApplianceCutouts,
  selectCustomCutouts,
  setApplianceCutouts,
  setCustomCutouts,
} from '~/redux/slices/cutout';
import {
  selectActiveDrawingObject,
  setActiveDrawingObject,
  updateNeedRedrawCanvas,
} from '~/redux/slices/drawingShape';
import { updateUserSelectedEdge } from '~/redux/slices/edge';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import {
  clearUndoStackState,
  pushOperationUndoStack,
  selectDrawingShapeElementsStack,
  selectMaxUndoIndex,
  selectUndoIndex,
  updateLatestPushedUserDesignOperation,
  updateUndoIndex,
} from '~/redux/slices/undoStack';

export const UndoStackContextProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const dispatch = useAppDispatch();

  const {
    shapesStack,
    setShapesStack,
    lastUserSelectedDesignOperation,
    latestOperationIsUndoOrRedo,
    latestOperationBeforeUndoRedoWasAddingAppliance,
    latestPushedUserDesignOperation,
  } = useUndoStack();

  const undoIndex = useAppSelector(selectUndoIndex);
  const maxUndoIndex = useAppSelector(selectMaxUndoIndex);
  const { selectedValue: selectedEdge } = useSelectedEdge();
  const selectedColorId = useAppSelector(selectSelectedColorId);
  const drawingShapeElementsStack = useAppSelector(
    selectDrawingShapeElementsStack
  );

  const currentActiveDrawingObject = useAppSelector(selectActiveDrawingObject);
  const selectedRanges = useAppSelector(
    selectSelectedAppliance('selectedRangeApplianceProduct')
  );
  const selectedCooktops = useAppSelector(
    selectSelectedAppliance('selectedCooktopApplianceProduct')
  );
  const selectedCustomCutouts = useAppSelector(selectCustomCutouts);
  const selectedAppliancesCutouts = useAppSelector(selectApplianceCutouts);
  const canvas = useAppSelector(selectFabricCanvas);

  const {
    getters: { areThereShapesInTheDrawingArea, drawingShapes },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const { removeApplianceFromDrawingArea } = useAppliance();

  const pushToUndoStack = useCallback(() => {
    const shapes = drawingTool.cloneShapes(drawingShapes);

    if (
      !areThereShapesInTheDrawingArea ||
      !selectedEdge ||
      !selectedColorId ||
      shapes.length <= 1
    ) {
      return;
    }

    const index = moveIndex('fwd', undoIndex) as number;
    const updateStack = addToStack(index);

    dispatch(updateUndoIndex(index));
    setShapesStack(updateStack(shapesStack, shapes));
    batch(() => {
      dispatch(
        pushOperationUndoStack({
          edge: selectedEdge,
          colorId: selectedColorId,
          activeDrawingObject: currentActiveDrawingObject,
          selectedCooktops,
          selectedRanges,
          selectedCustomCutouts,
          selectedAppliancesCutouts,
        })
      );
    });
  }, [
    currentActiveDrawingObject,
    areThereShapesInTheDrawingArea,
    dispatch,
    drawingShapes,
    selectedAppliancesCutouts,
    selectedColorId,
    selectedCooktops,
    selectedCustomCutouts,
    selectedEdge,
    selectedRanges,
    setShapesStack,
    shapesStack,
    undoIndex,
  ]);

  const pullFromUndoStack = useCallback(
    (operation: DoOperation) => {
      const index = moveIndex(operation === 'undo' ? 'bwd' : 'fwd', undoIndex);

      if (index === undefined) {
        return;
      }
      const {
        activeDrawingObject,
        edge,
        colorId,
        selectedAppliancesCutouts,
        selectedCustomCutouts,
        selectedCooktops,
        selectedRanges,
      } = drawingShapeElementsStack[index];

      const shapes = drawingTool
        .cloneShapes(shapesStack[index])
        .filter((shape) => !!shape);

      if (
        operation === 'undo' &&
        latestOperationBeforeUndoRedoWasAddingAppliance &&
        currentActiveDrawingObject?.previousObject?.object?.type === 'appliance'
      ) {
        removeApplianceFromDrawingArea(
          activeDrawingObject?.previousObject?.object?.id
        );
      }

      batch(() => {
        dispatch(updateUndoIndex(index));
        dispatch(setActiveDrawingObject(activeDrawingObject?.object));
        dispatch(updateUserSelectedEdge(edge));
        dispatch(updateSelectedColorId(colorId));
        dispatch(setApplianceCutouts(selectedAppliancesCutouts));
        dispatch(setCustomCutouts(selectedCustomCutouts));
        dispatch(
          setCookingAppliance({
            selectedType: 'selectedCooktopApplianceProduct',
            selectedAppliances: selectedCooktops,
          })
        );
        dispatch(
          setCookingAppliance({
            selectedType: 'selectedRangeApplianceProduct',
            selectedAppliances: selectedRanges,
          })
        );
      });

      updateDrawingShapes(shapes);

      // Clean up any orphaned canvas appliances that don't exist in the new data model
      // This is critical after undo/redo because shapes are cloned with new references
      if (canvas) {
        validateApplianceConsistency(shapes, canvas);
      }

      dispatch(updateNeedRedrawCanvas(true));
    },
    [
      canvas,
      currentActiveDrawingObject?.previousObject?.object?.type,
      dispatch,
      drawingShapeElementsStack,
      latestOperationBeforeUndoRedoWasAddingAppliance,
      removeApplianceFromDrawingArea,
      shapesStack,
      undoIndex,
      updateDrawingShapes,
    ]
  );

  const clearUndoStack = () => {
    setShapesStack([]);
    dispatch(clearUndoStackState());
  };

  useEffect(() => {
    const operation = lastUserSelectedDesignOperation;

    if (!operation) {
      return;
    }

    if (latestOperationIsUndoOrRedo) {
      dispatch(
        updateLatestPushedUserDesignOperation({
          operation,
          isUndoRedo: true,
        })
      );

      return;
    }

    if (latestPushedUserDesignOperation !== operation) {
      pushToUndoStack();
      dispatch(
        updateLatestPushedUserDesignOperation({ operation, isUndoRedo: false })
      );
    }
  }, [
    areThereShapesInTheDrawingArea,
    drawingShapes,
    lastUserSelectedDesignOperation,
    latestPushedUserDesignOperation,
    selectedEdge,
    selectedColorId,
    latestOperationIsUndoOrRedo,
    dispatch,
    pushToUndoStack,
  ]);

  const stackContextValue: UndoStackContextType = {
    pushToUndoStack,
    pullFromUndoStack,
    clearUndoStack,
    shapesStack,
    undoIndex,
    maxUndoIndex,
  };

  return (
    <UndoStackContext.Provider value={stackContextValue}>
      {children}
    </UndoStackContext.Provider>
  );
};
